import { z } from "zod"
import { type VotingListType, VotingListSchema } from "@antipartido-inc/contracts-ifaces"
import { Voting } from "./Voting.js"
import { Pagination } from "../../_kernel/Pagination.js"

const VotingListValidation = z.object({
  items: z.array(z.instanceof(Voting)),
  pagination: z.instanceof(Pagination),
})

export class VotingList {
  constructor(
    private readonly _items: Voting[],
    private readonly _pagination: Pagination,
  ) {}

  static create({ items, pagination }: z.infer<typeof VotingListValidation>) {
    const { success, error } = VotingListValidation.safeParse({ items, pagination })
    if (!success) throw new Error(error.message)
    return new VotingList(items, pagination)
  }

  static createFromAPI({ items, metadata }: VotingListType) {
    const { success, error } = VotingListSchema.safeParse({ items, metadata })
    if (!success) throw new Error(error.message)

    const votings = items.map((item) => Voting.createFromAPI(item))
    const pagination = Pagination.createFromAPI(metadata)

    return VotingList.create({ items: votings, pagination })
  }

  get items() { return this._items } // prettier-ignore
  get pagination() { return this._pagination } // prettier-ignore

  getById(id: string): Voting | undefined {
    return this._items.find((voting) => voting.id === id)
  }

  getEligibleItems(): readonly Voting[] {
    return this._items.filter((voting) => voting.canVote || voting.hasVoted)
  }

  hasAnyCannotVote(): boolean {
    return this._items.some((voting) => !voting.canVote)
  }

  hasActiveVotings(): boolean {
    return this._items.some((voting) => !voting.isFinished)
  }

  serialize() {
    return {
      items: this.items.map((voting) => voting.serialize()),
      pagination: this.pagination.serialize(),
    }
  }
}

export type VotingListSerialized = ReturnType<VotingList["serialize"]>
